import createDebug from '../utils/debug';
import { REDUNDANT_DEPENDENCIES } from '../constants';
import { isDefined } from '../utils/validators';
import getGlobalValue from '../utils/get-global-value';
import { loadTranslations } from '../utils/translations';
import { WrappedError, toError } from '../utils/custom-errors';
import {
  loadAssetsSequentially,
  loadAssetsConcurrently,
} from '../utils/asset-loaders';
import { createStyleEntry, createScriptEntry } from './asset-entry-creators';
import type {
  Manifest,
  MicroAppModule,
  TranslationEntry,
} from '../shared/types';

const debug = createDebug('manifest:assets-loader');

type TranslationEntryRef = NonNullable<Manifest['translation']>;

const getTranslationEntries = (manifest: Manifest): TranslationEntryRef[] =>
  manifest.translations ||
  (manifest.translation && [manifest.translation]) ||
  [];

const loadTranslationDataList = (
  translationEntries: TranslationEntryRef[],
  baseURL: string,
  language: string
): Promise<TranslationEntry[]> =>
  Promise.all(
    translationEntries.map(({ template, namespace }) => {
      const urlTemplate = baseURL + template;
      return loadTranslations(urlTemplate, language).then((data) => ({
        data,
        namespace,
      }));
    })
  );

export default async function loadAppAssets(
  appName: string,
  manifest: Manifest,
  language: string
): Promise<{ App: MicroAppModule; translationDataList: TranslationEntry[] }> {
  // load external dependencies
  if (manifest.externalDependencies) {
    debug('load external dependencies', appName, manifest.externalDependencies);
    const filteredExternalDependencies = manifest.externalDependencies.filter(
      (exDep) => !REDUNDANT_DEPENDENCIES.some((reDep) => exDep.match(reDep))
    );

    try {
      await loadAssetsSequentially(filteredExternalDependencies);
    } catch (error) {
      throw new WrappedError(
        `Loading external dependencies of ${appName} failed`,
        toError(error)
      );
    }
  }

  // load translation files (multiple entries possible)
  let translationDataList: TranslationEntry[];
  try {
    translationDataList = await loadTranslationDataList(
      getTranslationEntries(manifest),
      manifest.baseURL,
      language
    );
  } catch (error) {
    throw new WrappedError(
      `Loading translations of ${appName} failed`,
      toError(error)
    );
  }

  // load style and script
  const maybeStyleEntry = createStyleEntry(manifest);
  const scriptEntry = createScriptEntry(manifest);

  const assetEntries = [maybeStyleEntry, scriptEntry].filter(isDefined); // reject undefined entries

  debug('load js / css assets', appName, assetEntries);
  try {
    await loadAssetsConcurrently(assetEntries);
  } catch (error) {
    throw new WrappedError(
      `Loading App assets of ${appName} failed`,
      toError(error)
    );
  }

  const App = getGlobalValue(manifest.exposed) as MicroAppModule | undefined;

  if (!App) {
    throw new Error(`${manifest.exposed} is not defined`);
  }

  return { App, translationDataList };
}
